import { useEffect, useState } from "react";
import { Button, Menu, Stack, Typography } from "@mui/material";

import { TextField } from "../../../components";
import { useAppDispatch } from "../../../store/hooks";
import {
  createTagThunk,
  deleteTagThunk,
  updateTagThunk,
} from "../../../features/settings/settings.thunk";
import { ITag } from "../../../features/settings/settings.types";
import { colors as embedColors } from "../../ProductAnalyzer/AmazonEmbededView/config";

interface ITagMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  tag: ITag | null;
  onClose: () => void;
  isEmbed?: boolean;
}

export const TagMenu: React.FC<ITagMenuProps> = ({
  open,
  anchorEl,
  tag,
  onClose,
  isEmbed = false,
}) => {
  const dispatch = useAppDispatch();
  const [caption, setCaption] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  useEffect(() => {
    setCaption(tag?.caption || "");
  }, [tag]);

  const handleCaptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCaption(event.target.value);
  };

  const handleSave = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!caption) return;
    setIsSaving(true);
    if (!tag) {
      dispatch(createTagThunk({ caption }))
        .unwrap()
        .then(() => {
          onClose();
          setCaption("");
        })
        .finally(() => {
          setIsSaving(false);
        });
    } else {
      dispatch(updateTagThunk({ id: tag.id, caption }))
        .unwrap()
        .then(() => {
          onClose();
          setCaption("");
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!tag) return;
    setIsDeleting(true);
    dispatch(deleteTagThunk(tag.id))
      .unwrap()
      .then(() => {
        onClose();
      })
      .catch((e) => {})
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const containedButtonStyle = isEmbed ? {
    backgroundColor: embedColors.primary.main,
    border: `1px solid ${embedColors.primary.main}`,
    color: embedColors.text.white,
    fontSize: "11.3px",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: embedColors.primary.hover,
      borderColor: embedColors.primary.hover,
    },
    "&.Mui-disabled": {
      backgroundColor: embedColors.background.gray,
      borderColor: embedColors.border.light,
      color: embedColors.text.disabled,
      opacity: 0.6,
    },
  } : undefined;

  const outlinedButtonStyle = isEmbed ? {
    color: embedColors.primary.main,
    border: `1px solid ${embedColors.primary.main}`,
    fontSize: "11.3px",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: embedColors.primary.hover,
      borderColor: embedColors.primary.hover,
      color: embedColors.text.white,
    },
  } : undefined;

  return (
    <Menu
      id="tag-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{
        list: {
          "aria-labelledby": anchorEl?.id,
        },
      }}
    >
      <Stack width={"120px"} gap={"5px"} padding={"0 8px"} textAlign={"center"}>
        <Typography variant="subtitle1">{tag ? "Edit" : "New"} Tag</Typography>
        <TextField 
          value={caption} 
          onChange={handleCaptionChange} 
          autoFocus
          sx={
            isEmbed
              ? {
                  "& .MuiInputBase-input": {
                    backgroundColor: embedColors.background.white,
                    borderColor: embedColors.border.light,
                    color: embedColors.text.primary,
                    "&:hover": {
                      borderColor: embedColors.primary.main,
                    },
                    "&:focus": {
                      borderColor: embedColors.primary.main,
                      outline: "none",
                      boxShadow: `0 0 0 3px ${embedColors.primary.light}`,
                    },
                  },
                }
              : undefined
          }
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={!caption || (!!tag && tag.caption === caption)}
          loading={isSaving}
          sx={containedButtonStyle}
        >
          Save
        </Button>
        {!!tag && (
          <Button
            variant="contained"
            size="small"
            onClick={handleDelete}
            loading={isDeleting}
            sx={containedButtonStyle}
          >
            Delete
          </Button>
        )}
        <Button 
          variant="outlined" 
          size="small" 
          onClick={onClose}
          sx={outlinedButtonStyle}
        >
          Cancel
        </Button>
      </Stack>
    </Menu>
  );
};

export default TagMenu;
